// frontend/src/utils/duplicateMcNotice.js
import { formatDate } from './dateUtils';

const CONSUMED_STATUSES = new Set(['approved', 'auto_approved', 'verified']);
const CLOSED_CURRENT_STATUSES = new Set(['rejected', 'cancelled']);

/**
 * Explain a same-certificate conflict in the MC verification modal.
 *
 * A sick day is used only when an application is approved. A rejected
 * application does not mention another draft: that draft is a different
 * task, and this screen can no longer verify anything.
 *
 * @param {object|null} conflict Serialized duplicate from the verification API.
 * @param {{ currentStatus?: string }} [context]
 * @returns {{ tone: 'error'|'warning'|'info', title: string, body: string }|null}
 */
export function describeDuplicateMcNotice(conflict, context = {}) {
  if (!conflict) return null;

  const currentStatus = context.currentStatus || '';
  const otherConsumed = CONSUMED_STATUSES.has(conflict.status);
  const currentClosed = CLOSED_CURRENT_STATUSES.has(currentStatus);
  const otherApplication = applicationPhrase(conflict);

  if (currentClosed && !otherConsumed) {
    const action = currentStatus === 'cancelled' ? 'Cancelling' : 'Rejecting';
    return {
      tone: 'info',
      title: 'No sick day was used',
      body: `${action} this application did not take a sick day.`,
    };
  }

  if (otherConsumed && currentClosed) {
    const action = currentStatus === 'cancelled' ? 'Cancelling' : 'Rejecting';
    return {
      tone: 'info',
      title: 'No extra sick day was used',
      body: `${action} this application did not take a sick day. The sick day is already on approved ${otherApplication}.`,
    };
  }

  if (otherConsumed) {
    return {
      tone: 'error',
      title: 'This certificate is already used',
      body: `It is already on approved ${otherApplication}. Verifying this one as well would use another sick day. Close this, or reject it.`,
    };
  }

  return {
    tone: 'warning',
    title: 'This certificate is on another application',
    body: `It is also on ${otherApplication}, which is ${openStatePhrase(conflict.status)}. No sick day has been used yet. Verify only one. Verifying both would use two sick days.`,
  };
}

function applicationPhrase(conflict) {
  const leaveType = conflict.leave_type || 'sick leave';
  const id = conflict.application_id;
  return `${leaveType} #${id}${periodPhrase(conflict)}`;
}

function periodPhrase(conflict) {
  if (!conflict.start_date || !conflict.end_date) return '';
  const start = formatDate(conflict.start_date);
  const end = formatDate(conflict.end_date);
  if (!start || start === 'N/A' || start === 'Invalid Date') return '';
  if (start === end) return ` (${start})`;
  return ` (${start} – ${end})`;
}

function openStatePhrase(status) {
  if (status === 'draft' || status === 'submitted' || status === 'pending') {
    return 'not approved yet';
  }
  return 'still open';
}
